/**
 * Admin-only feedback dashboard.
 *
 * Two halves:
 *   - Summary cards (total notes / overall rating)
 *   - Page-by-page rollup table (count, avg rating, latest_at)
 *   - Recent feedback list (filterable by page)
 *
 * Backed by /api/admin/feedback/stats + /api/admin/feedback.
 * Only visible to admin accounts (`_get_current_admin` returns 403).
 */
import React, { useEffect, useMemo, useState } from "react";
import Navbar from "../components/Navbar";
import AdminHubNav from "../components/AdminHubNav";
import { api, friendlyAxiosError } from "../lib/api";
import { Loader, MessageSquare, Star, RefreshCw, Filter } from "lucide-react";

function StarRow({ value }) {
  if (value == null) return <span className="font-mono-rm text-xs" style={{ color: "#94A3B8" }}>—</span>;
  const full = Math.round(value);
  return (
    <span className="inline-flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((n) => (
        <Star key={n} size={12}
              fill={n <= full ? "#FFC145" : "transparent"}
              style={{ color: n <= full ? "#FFC145" : "#CBD5E1" }} />
      ))}
      <span className="font-mono-rm text-[10px] uppercase tracking-widest ml-1" style={{ color: "#475569" }}>
        {Number(value).toFixed(2)}
      </span>
    </span>
  );
}

function ratingTone(value) {
  if (value == null) return { color: "#94A3B8", soft: "#F1F5F9" };
  if (value >= 4.5)   return { color: "#0E5A4A", soft: "#CFFAFE" };
  if (value >= 3.5)   return { color: "#1E40AF", soft: "#DBEAFE" };
  if (value >= 2.5)   return { color: "#CA8A04", soft: "#FEF3C7" };
  return                       { color: "#B91C1C", soft: "#FEE2E2" };
}

export default function AdminFeedback() {
  const [bugsOnly, setBugsOnly] = useState(false);
  const [stats, setStats] = useState(null);
  const [items, setItems] = useState([]);
  const [exitData, setExitData] = useState(null);
  const [pageFilter, setPageFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const reload = async () => {
    setLoading(true);
    setErr("");
    try {
      const [s, list, exits] = await Promise.all([
        api.get("/admin/feedback/stats"),
        api.get("/admin/feedback", { params: { page: pageFilter || undefined, limit: 200 } }),
        api.get("/admin/exit-surveys"),
      ]);
      setStats(s.data);
      setItems(list.data.items || []);
      setExitData(exits.data);
    } catch (e) {
      setErr(friendlyAxiosError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [pageFilter]);

  const byPage = useMemo(() => stats?.by_page || [], [stats]);

  return (
    <div className="min-h-screen rm-paper" data-testid="admin-feedback-page">
      <Navbar />
      <div className="max-w-6xl mx-auto px-4 md:px-8 py-6 md:py-8">
        <AdminHubNav active="feedback" />
        {/* Header */}
        <div className="flex items-end justify-between flex-wrap gap-3 mb-5">
          <div>
            <div className="font-mono-rm uppercase text-[10px] tracking-widest rm-text-orange mb-1"
                 style={{ fontWeight: 700 }}>
              · Admin · Feedback rollup
            </div>
            <h1 className="font-display uppercase leading-tight"
                style={{ fontSize: "clamp(1.6rem, 3vw, 2.2rem)", color: "#0F1B2D", letterSpacing: "-0.02em" }}>
              What users are telling Resmo.
            </h1>
          </div>
          <button onClick={reload} className="rm-btn rm-btn-ghost text-xs px-3 py-2"
                  data-testid="admin-feedback-reload">
            {loading ? <Loader size={13} className="animate-spin" /> : <RefreshCw size={13} />}
            Reload
          </button>
        </div>

        {err && (
          <div className="rm-card-static p-3 mb-4" style={{ background: "#FEF2F2", border: "2px solid #B91C1C" }}>
            <div className="text-sm font-mono-rm" style={{ color: "#991B1B" }}>· {err}</div>
          </div>
        )}

        {/* Summary cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4 mb-6" data-testid="admin-feedback-summary">
          <div className="rm-card-static p-4 md:p-5"
               style={{ background: "white", borderColor: "#0F1B2D", boxShadow: "5px 5px 0 0 #FF6A00" }}>
            <div className="font-mono-rm uppercase text-[10px] tracking-widest" style={{ color: "#FF6A00", fontWeight: 700 }}>
              · Total notes
            </div>
            <div className="font-display mt-1" style={{ fontSize: "2.4rem", color: "#0F1B2D", letterSpacing: "-0.02em" }}>
              {stats?.total ?? "—"}
            </div>
            <div className="font-mono-rm text-[10px] uppercase tracking-widest" style={{ color: "#475569" }}>
              · {stats?.rated_count ?? 0} with a star rating
            </div>
          </div>

          <div className="rm-card-static p-4 md:p-5"
               style={{ background: "white", borderColor: "#0F1B2D", boxShadow: "5px 5px 0 0 #FFC145" }}>
            <div className="font-mono-rm uppercase text-[10px] tracking-widest" style={{ color: "#CA8A04", fontWeight: 700 }}>
              · Overall rating
            </div>
            <div className="font-display mt-1 flex items-center gap-2"
                 style={{ fontSize: "2.4rem", color: "#0F1B2D", letterSpacing: "-0.02em" }}>
              {stats?.overall_avg != null ? stats.overall_avg.toFixed(2) : "—"}
              <span className="font-mono-rm uppercase text-[10px] tracking-widest" style={{ color: "#475569" }}>/ 5</span>
            </div>
            <StarRow value={stats?.overall_avg} />
          </div>

          <div className="rm-card-static p-4 md:p-5"
               style={{ background: "white", borderColor: "#0F1B2D", boxShadow: "5px 5px 0 0 #1E40AF" }}>
            <div className="font-mono-rm uppercase text-[10px] tracking-widest" style={{ color: "#1E40AF", fontWeight: 700 }}>
              · Distinct pages
            </div>
            <div className="font-display mt-1" style={{ fontSize: "2.4rem", color: "#0F1B2D", letterSpacing: "-0.02em" }}>
              {byPage.length}
            </div>
            <div className="font-mono-rm text-[10px] uppercase tracking-widest" style={{ color: "#475569" }}>
              · ordered by note count below
            </div>
          </div>
        </div>

        {/* Exit survey rollup — 3-question beta wrap-up survey */}
        <div className="rm-card-static p-0 mb-6 overflow-hidden"
             style={{ background: "white", borderColor: "#0F1B2D" }}
             data-testid="admin-exit-surveys">
          <div className="px-4 py-3 flex items-center justify-between"
               style={{ borderBottom: "2px solid #0F1B2D", background: "#0E5A4A", color: "white" }}>
            <div className="font-display uppercase text-sm tracking-wide">Beta exit survey</div>
            <span className="font-mono-rm uppercase text-[10px] tracking-widest" style={{ color: "#FFC145" }}>
              {exitData?.stats?.total ?? 0} responses
              {exitData?.stats?.avg_rating != null ? ` · avg ${exitData.stats.avg_rating}/5` : ""}
              {exitData?.stats?.total
                ? ` · recommend: ${exitData.stats.recommend.yes} yes / ${exitData.stats.recommend.maybe} maybe / ${exitData.stats.recommend.no} no`
                : ""}
            </span>
          </div>
          {(exitData?.responses || []).length === 0 ? (
            <div className="text-center py-6 font-mono-rm text-xs" style={{ color: "#94A3B8" }}>
              · No exit-survey responses yet — testers get the link in the wrap-up email.
            </div>
          ) : (
            <div className="divide-y" style={{ borderColor: "#E2E8F0" }}>
              {exitData.responses.map((r) => (
                <div key={r.id} className="px-4 py-3 flex items-start justify-between gap-3 flex-wrap"
                     data-testid={`exit-response-${r.id}`}>
                  <div className="min-w-[220px]">
                    <div className="text-[13px] font-semibold" style={{ color: "#0F1B2D" }}>
                      {r.name || r.email}
                    </div>
                    <div className="font-mono-rm text-[10px]" style={{ color: "#94A3B8" }}>
                      {r.email} · {r.updated_at ? new Date(r.updated_at).toLocaleString() : "—"}
                    </div>
                    {r.comment && (
                      <p className="text-[12.5px] mt-1.5 leading-snug" style={{ color: "#334155", maxWidth: 560 }}>
                        “{r.comment}”
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <StarRow value={r.rating} />
                    <span className="rm-tag text-[10px]"
                          style={r.recommend === "yes"
                            ? { background: "#D1FAE5", color: "#065F46", borderColor: "#0E5A4A" }
                            : r.recommend === "no"
                              ? { background: "#FEE2E2", color: "#991B1B", borderColor: "#991B1B" }
                              : { background: "#FEF3C7", color: "#92400E", borderColor: "#CA8A04" }}>
                      {r.recommend === "yes" ? "Would recommend" : r.recommend === "no" ? "Not yet" : "Maybe"}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* By-page rollup table */}
        <div className="rm-card-static p-0 mb-6 overflow-hidden"
             style={{ background: "white", borderColor: "#0F1B2D" }}
             data-testid="admin-feedback-by-page">
          <div className="px-4 py-3 flex items-center justify-between"
               style={{ borderBottom: "2px solid #0F1B2D", background: "#0F1B2D", color: "white" }}>
            <div className="font-display uppercase text-sm tracking-wide">By page</div>
            <span className="font-mono-rm uppercase text-[10px] tracking-widest" style={{ color: "#FFC145" }}>
              · click a row to filter feedback below
            </span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="admin-feedback-by-page-table">
              <thead>
                <tr className="font-mono-rm uppercase text-[10px] tracking-widest"
                    style={{ color: "#475569", background: "#F8FAFC", borderBottom: "2px dashed #CBD5E1" }}>
                  <th className="text-left px-3 py-2">Page</th>
                  <th className="text-left px-3 py-2">Notes</th>
                  <th className="text-left px-3 py-2">Avg rating</th>
                  <th className="text-left px-3 py-2">Latest</th>
                  <th className="text-left px-3 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {byPage.length === 0 && !loading && (
                  <tr><td colSpan={5} className="text-center py-6 font-mono-rm text-xs" style={{ color: "#94A3B8" }}>
                    · No feedback yet.
                  </td></tr>
                )}
                {byPage.map((row, i) => {
                  const tone = ratingTone(row.avg_rating);
                  const active = pageFilter === row.page;
                  return (
                    <tr key={i}
                        className="cursor-pointer transition-colors"
                        onClick={() => setPageFilter(active ? "" : row.page)}
                        style={{
                          borderBottom: "1px dashed #E5E7EB",
                          background: active ? "#FFF2EC" : "white",
                        }}
                        data-testid={`admin-feedback-row-${i}`}>
                      <td className="px-3 py-2.5">
                        <div className="font-bold text-sm">{row.page_label || row.page || "(unknown)"}</div>
                        <div className="font-mono-rm text-[10px]" style={{ color: "#94A3B8" }}>
                          {row.page || "—"}
                        </div>
                      </td>
                      <td className="px-3 py-2.5 font-display text-base" style={{ color: "#0F1B2D" }}>
                        {row.count}
                      </td>
                      <td className="px-3 py-2.5">
                        <span className="inline-flex items-center gap-2 px-2 py-0.5"
                              style={{ background: tone.soft, color: tone.color, border: `2px solid ${tone.color}`, borderRadius: 999 }}>
                          <StarRow value={row.avg_rating} />
                        </span>
                      </td>
                      <td className="px-3 py-2.5 font-mono-rm text-[11px]" style={{ color: "#475569" }}>
                        {row.latest_at ? new Date(row.latest_at).toLocaleString() : "—"}
                      </td>
                      <td className="px-3 py-2.5 text-right font-mono-rm text-[10px] uppercase tracking-widest"
                          style={{ color: active ? "#FF6A00" : "#94A3B8" }}>
                        {active ? "Clear filter ✕" : "Filter →"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Recent feedback list */}
        <div className="rm-card-static p-0 overflow-hidden"
             style={{ background: "white", borderColor: "#0F1B2D" }}
             data-testid="admin-feedback-list">
          <div className="px-4 py-3 flex items-center justify-between"
               style={{ borderBottom: "2px solid #0F1B2D", background: "#0F1B2D", color: "white" }}>
            <div className="font-display uppercase text-sm tracking-wide">
              Recent notes {pageFilter && (
                <span style={{ color: "#FFC145" }}>· filtered to {pageFilter}</span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setBugsOnly(!bugsOnly)}
                className="font-mono-rm uppercase text-[10px] tracking-widest px-2 py-1"
                style={{
                  background: bugsOnly ? "#B91C1C" : "transparent",
                  border: "2px solid #B91C1C",
                  color: bugsOnly ? "white" : "#FCA5A5",
                  borderRadius: 4,
                }}
                data-testid="admin-feedback-bugs-only">
                {bugsOnly ? "Bugs only ✓" : "Bugs only"}
              </button>
              <Filter size={12} style={{ color: "#FFC145" }} />
              <span className="font-mono-rm uppercase text-[10px] tracking-widest" style={{ color: "#FFC145" }}>
                {items.filter((it) => !bugsOnly || it.kind === "bug").length} shown
              </span>
              {pageFilter && (
                <button
                  type="button"
                  onClick={() => setPageFilter("")}
                  className="font-mono-rm uppercase text-[10px] tracking-widest underline"
                  style={{ color: "#FFC145" }}
                  data-testid="admin-feedback-clear-filter">
                  Clear
                </button>
              )}
            </div>
          </div>
          <div className="divide-y" style={{ borderColor: "#E5E7EB" }}>
            {items.length === 0 && !loading && (
              <div className="text-center py-6 font-mono-rm text-xs" style={{ color: "#94A3B8" }}>
                · No feedback yet for this filter.
              </div>
            )}
            {items.filter((it) => !bugsOnly || it.kind === "bug").map((it, i) => {
              const tone = it.kind === "bug"
                ? { color: "#B91C1C", soft: "#FEE2E2" }
                : ratingTone(it.rating);
              return (
                <div key={i} className="px-4 py-3 flex gap-4 items-start"
                     data-testid={`admin-feedback-item-${i}`}>
                  <div className="flex-shrink-0">
                    <div className="rounded-full flex items-center justify-center"
                         style={{ width: 36, height: 36, background: tone.soft, border: `2px solid ${tone.color}`, color: tone.color }}>
                      <MessageSquare size={15} />
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className="font-bold text-sm">{it.name || it.email || "Anonymous"}</span>
                      {it.kind === "bug" && (
                        <span className="font-mono-rm uppercase text-[10px] tracking-widest"
                              style={{ background: "#FEE2E2", padding: "2px 6px", border: "2px solid #B91C1C", borderRadius: 4, color: "#B91C1C", fontWeight: 700 }}
                              data-testid={`admin-feedback-bug-tag-${i}`}>
                          Bug
                        </span>
                      )}
                      <span className="font-mono-rm uppercase text-[10px] tracking-widest"
                            style={{ background: "#F1F5F9", padding: "2px 6px", border: "2px solid #CBD5E1", borderRadius: 4, color: "#475569" }}>
                        {it.page_label || it.page || "—"}
                      </span>
                      <StarRow value={it.rating} />
                    </div>
                    <p className="text-sm whitespace-pre-wrap" style={{ color: "#0F1B2D" }}>
                      {it.message}
                    </p>
                    <div className="font-mono-rm text-[10px] uppercase tracking-widest mt-1.5" style={{ color: "#94A3B8" }}>
                      · {it.created_at ? new Date(it.created_at).toLocaleString() : "—"}
                      {it.page && <span style={{ marginLeft: 8 }}>· {it.page}</span>}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
